import { getPortfolioFileContentBlob, type CoworkArtifact, type CoworkPortfolioFile } from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { FileInspection } from "./file-inspection"
import { Dialog } from "@kobalte/core/dialog"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { createResource, ErrorBoundary, lazy, Match, onCleanup, Show, Suspense, Switch } from "solid-js"
import "./file-inspection.css"

const ReportPreview = lazy(() =>
  import("@/pages/session/v2/cowork-files-panel").then((module) => ({ default: module.ReportPreview })),
)

export function CoworkFilePreview(props: {
  file: CoworkPortfolioFile
  onClose: () => void
  onDownload: () => void
  downloadError?: string
}) {
  const language = useLanguage()
  const opener = document.activeElement
  const previewKey = () => `${props.file.company_id}:${props.file.project_id}:${props.file.id}:${props.file.updated_at}`
  const [preview, { refetch }] = createResource(
    () => ({ key: previewKey(), file: { ...props.file } }),
    async ({ key, file }) => {
      const blob = await getPortfolioFileContentBlob(file.id, file.company_id)
      const format = previewFormat(file, blob)
      const text = format === "html" || format === "text" || format === "json" ? await blob.text() : ""
      return { key, blob, format, text }
    },
  )
  const currentPreview = () => preview()?.key === previewKey() ? preview() : undefined

  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay class="fixed inset-0 z-[110] bg-v2-overlay-simple-overlay-scrim" />
        <Dialog.Content
          class="cowork-file-preview fixed inset-0 z-[111] flex h-dvh w-full flex-col overflow-hidden bg-v2-background-bg-base text-v2-text-text-base outline-none"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
          }}
        >
          <header class="file-preview-header flex shrink-0 items-center justify-between gap-4 border-b border-v2-border-border-base px-5 py-4">
            <div class="min-w-0">
              <Dialog.Title class="file-preview-title" title={props.file.filename}>{props.file.filename}</Dialog.Title>
              <Dialog.Description class="file-preview-context text-v2-text-text-muted">
                {props.file.company_name} · {props.file.project_name}
                <Show when={props.file.period_name}> · {props.file.period_name}</Show>
              </Dialog.Description>
            </div>
            <div class="flex shrink-0 gap-2">
              <button type="button" class="file-preview-download"
                aria-label={language.t("cowork.enterprise.files.download")}
                title={language.t("cowork.enterprise.files.download")}
                onClick={props.onDownload}
              ><IconV2 name="download" size="small" /><span class="file-preview-download-label">{language.t("cowork.enterprise.files.download")}</span></button>
              <Dialog.CloseButton
                class="file-preview-download"
                aria-label={language.t("common.close")}
                title={language.t("common.close")}
              >
                {language.t("common.close")}
              </Dialog.CloseButton>
            </div>
          </header>
          <Show when={props.downloadError}>
            <div role="alert" class="bg-v2-state-bg-danger px-5 py-3 text-[12px] text-v2-state-fg-danger">
              {props.downloadError}
            </div>
          </Show>
          <div class="flex min-h-0 flex-1 bg-v2-background-bg-layer-02">
            <FileInspection
              file={props.file}
              initialMode="split"
              document={() => (
                <div class="file-preview-document-shell h-full">
                  <ErrorBoundary fallback={(_error, reset) => <PreviewError onRetry={() => { reset(); void refetch() }} />}>
                    <Suspense fallback={<PreviewLoading />}>
                      <Show when={!preview.error} fallback={<PreviewError onRetry={() => void refetch()} />}>
                        <Show when={currentPreview()} keyed fallback={<PreviewLoading />}>
                          {(value) => <FileContent file={props.file} {...value} />}
                        </Show>
                      </Show>
                    </Suspense>
                  </ErrorBoundary>
                </div>
              )}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}

function PreviewError(props: { onRetry?: () => void }) {
  const language = useLanguage()
  return (
    <div role="alert" class="file-preview-state text-v2-text-text-muted">
      <p>{language.t("cowork.files.panel.noPreview")}</p>
      <Show when={props.onRetry}><button class="file-preview-download" type="button" onClick={props.onRetry}>{language.t("cowork.enterprise.workflows.reporting.retry")}</button></Show>
    </div>
  )
}

function PreviewLoading() {
  const language = useLanguage()
  return (
    <div role="status" class="file-preview-state text-v2-text-text-muted">
      {language.t("cowork.files.panel.loadingPreview")}
    </div>
  )
}

function FileContent(props: { file: CoworkPortfolioFile; blob: Blob; format: string; text: string }) {
  const language = useLanguage()
  const url =
    props.format === "pdf"
      ? URL.createObjectURL(new Blob([props.blob], { type: "application/pdf" }))
      : props.format === "image"
        ? URL.createObjectURL(props.blob)
        : ""
  onCleanup(() => {
    if (url) URL.revokeObjectURL(url)
  })
  const office = /^(xlsx|xlsm|xls|pptx|docx)$/.test(props.format)
  // Adapt source-file metadata for the same read-only Office renderer used by Reports.
  const artifact: CoworkArtifact = {
    ...props.file,
    title: props.file.filename,
    format: props.format === "xls" ? "xlsx" : props.format,
    artifact_type: "file",
    artifact_metadata: {},
    report_status: "draft",
    validation_status: "incomplete",
    entity_scope: {},
    source_coverage: {},
    summary: {},
    exceptions: [],
    validation_results: [],
    lineage: [],
    mapping_coverage_details: {},
  }

  return (
    <Switch
      fallback={
        <div class="grid h-full place-items-center text-[12px] text-v2-text-text-muted">
          {language.t("cowork.files.panel.noPreview")}
        </div>
      }
    >
      <Match when={props.format === "pdf"}>
        <iframe
          class="h-full w-full rounded-lg border border-v2-border-border-base bg-white"
          src={`${url}#view=FitH`}
          title={props.file.filename}
        />
      </Match>
      <Match when={props.format === "html"}>
        <iframe
          class="h-full w-full rounded-lg border border-v2-border-border-base bg-white"
          sandbox=""
          srcdoc={props.text}
          title={props.file.filename}
        />
      </Match>
      <Match when={props.format === "image"}>
        <div class="flex h-full items-center justify-center">
          <img src={url} alt={props.file.filename} class="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      </Match>
      <Match when={props.format === "text" || props.format === "json"}>
        <pre class="min-h-full whitespace-pre-wrap break-words rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-5 text-[12px] leading-5">
          {props.format === "json" ? formatJson(props.text) : props.text}
        </pre>
      </Match>
      <Match when={office}>
        <ReportPreview artifact={artifact} load={async () => ({ blob: props.blob, filename: props.file.filename })} />
      </Match>
    </Switch>
  )
}

function previewFormat(file: CoworkPortfolioFile, blob: Blob) {
  const extension = file.filename.split(".").pop()?.toLowerCase() ?? ""
  const mime = (blob.type || file.content_type).split(";")[0].toLowerCase()
  if (mime === "application/pdf" || extension === "pdf") return "pdf"
  if (mime.startsWith("image/") || /^(png|jpe?g|gif|webp|svg|bmp|ico|avif)$/.test(extension)) return "image"
  if (mime === "text/html" || /^(html|htm)$/.test(extension)) return "html"
  if (/^(xlsx|xlsm|xls|pptx|docx)$/.test(extension)) return extension
  if (mime.includes("spreadsheetml") || mime === "application/vnd.ms-excel") return "xlsx"
  if (mime.includes("presentationml")) return "pptx"
  if (mime.includes("wordprocessingml")) return "docx"
  if (mime === "application/json" || extension === "json") return "json"
  if (mime.startsWith("text/") || mime === "application/xml" || /^(txt|csv|tsv|xml|md|log|ya?ml)$/.test(extension))
    return "text"
  return "unsupported"
}

function formatJson(text: string) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}
